/* ── WORK_TRACK — Core ───────────────────────────────────────
   Shared state, DOM helpers, API client, theme, routing and UI
   primitives. Every feature module imports from here.
   ─────────────────────────────────────────────────────────── */

import type {
  Member,
  MeetingInvite,
  MeetingRoom,
  Project,
  Role,
  Task,
  TaskStatus,
  TaskType,
  User,
  ViewTab
} from './types';

export const $ = <T extends HTMLElement = HTMLElement>(sel: string, root?: ParentNode): T =>
  (root || document).querySelector(sel) as T;

export const $$ = <T extends HTMLElement = HTMLElement>(sel: string, root?: ParentNode): T[] =>
  Array.from((root || document).querySelectorAll(sel)) as T[];

export const esc = (s: unknown): string =>
  String(s == null ? '' : s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

/* ── Shared state ─────────────────────────────────────────── */

export interface AppState {
  token: string | null;
  user: User | null;
  projects: Project[];
  tasks: Task[];
  members: Member[];
  boardTasks: Task[];
  meetings: MeetingRoom[];
  view: ViewTab;
}

export const state: AppState = {
  token: null,
  user: null,
  projects: [],
  tasks: [],
  members: [],
  boardTasks: [],
  meetings: [],
  view: 'dashboard'
};

export interface PeerState {
  pc: RTCPeerConnection;
  name: string;
  ice: RTCIceCandidateInit[];
  mic: boolean;
  cam: boolean;
  hand: boolean;
  stream: MediaStream | null;
  failTimer?: number | null;
  killTimer?: number | null;
}

export interface JoinRequest {
  from: number;
  name: string;
}

export interface AudioLevel {
  analyser: AnalyserNode;
  data: Uint8Array<ArrayBuffer>;
}

// Shared runtimes (mutable state owned by the feature modules)
export interface MeetRuntime {
  ws: WebSocket | null;
  room: string | null;
  selfId: number | null;
  localStream: MediaStream | null;
  shareStream: MediaStream | null;
  peers: Map<string, PeerState>;
  media: { mic: boolean; cam: boolean };
  sharing: boolean;
  noAudio: boolean;
  organizerId: string | null;
  isOrganizer: boolean;
  handUp: boolean;
  waitingApproval: boolean;
  requests: JoinRequest[];
  chatWs: WebSocket | null;
  channel: string | null;
  myInvites: MeetingInvite[];
  _seenInviteIds: number[];
  _notifiedInviteIds: number[];
  audioCtx: AudioContext | null;
  audioLevels: Map<string, AudioLevel>;
  speakerTimer: number | null;
  currentSpeaker: string | null;
  speakingNow: string[];
  pipActive: boolean;
  pipDismissed: boolean;
  prevView: ViewTab | null;
  ringTimer: number | null;
  ringRoom: string | null;
  enterSeq: number;
}

export const meet: MeetRuntime = {
  ws: null,
  room: null,
  selfId: null,
  localStream: null,
  shareStream: null,
  peers: new Map(), // id -> PeerState
  media: { mic: true, cam: true },
  sharing: false,
  noAudio: false,
  organizerId: null,
  isOrganizer: false,
  handUp: false,
  waitingApproval: false,
  requests: [], // join requests awaiting host decision
  chatWs: null,
  channel: null,
  myInvites: [],
  _seenInviteIds: [],
  _notifiedInviteIds: [],
  audioCtx: null,
  audioLevels: new Map(), // id -> AudioLevel
  speakerTimer: null,
  currentSpeaker: null,
  speakingNow: [],
  pipActive: false,
  pipDismissed: false,
  prevView: null,
  ringTimer: null,
  ringRoom: null,
  enterSeq: 0
};

export interface ChatRuntime {
  ws: WebSocket | null;
  channel: string;
  reconnectTimer: number | null;
  explicitClose: boolean;
  pendingImage: string | null;
}

export const chatState: ChatRuntime = {
  ws: null,
  channel: '',
  reconnectTimer: null,
  explicitClose: false,
  pendingImage: null
};

export interface ActiveTimer {
  project_id: number | null;
  task_id: number | null;
  note: string;
  started_at: number;
}

export const timerState: { tick: number | null; active: ActiveTimer | null } = { tick: null, active: null };

export const TAB_LABELS: Record<string, string> = {
  dashboard: 'Dashboard',
  board: 'Board',
  timer: 'Timer',
  projects: 'Projects',
  entries: 'Time log',
  team: 'Team'
};
export const ROLE_LABEL: Record<Role, string> = { owner: 'Owner', admin: 'Admin', developer: 'Developer' };
export const TYPE_LABEL: Record<TaskType, string> = {
  task: 'To do',
  bug: 'Bug',
  feature: 'Feature',
  implementation: 'Implementation'
};
export const STATUS_LABEL: Record<TaskStatus, string> = {
  todo: 'To do',
  approved: 'Approved',
  in_progress: 'In progress',
  done: 'Done'
};

// ── SVG icons ─────────────────────────────────────────────
const ICON_PATHS: Record<string, string> = {
  dashboard:
    '<rect x="3" y="3" width="7" height="7" rx="1.5"/><rect x="14" y="3" width="7" height="7" rx="1.5"/><rect x="3" y="14" width="7" height="7" rx="1.5"/><rect x="14" y="14" width="7" height="7" rx="1.5"/>',
  board: '<rect x="3" y="3" width="18" height="18" rx="2"/><path d="M9 3v18"/><path d="M3 9h18"/>',
  chat: '<path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.4 8.4 0 0 1-3.8-.9L3 21l1.9-5.7a8.4 8.4 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>',
  meet: '<rect x="2" y="6" width="14" height="12" rx="2"/><path d="m22 8-6 4 6 4V8z"/>',
  timer: '<circle cx="12" cy="13" r="8"/><path d="M12 9v4l2.5 2.5"/><path d="M9 2h6"/>',
  projects: '<path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>',
  team: '<path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>',
  plus: '<path d="M12 5v14"/><path d="M5 12h14"/>',
  search: '<circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>',
  trash:
    '<path d="M3 6h18"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><path d="m10 11 4 4"/><path d="m14 11-4 4"/>',
  edit: '<path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/>',
  check: '<path d="M20 6 9 17l-5-5"/>',
  x: '<path d="M18 6 6 18"/><path d="m6 6 12 12"/>',
  clock: '<circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>',
  send: '<path d="m22 2-7 20-4-9-9-4Z"/><path d="M22 2 11 13"/>',
  chevronDown: '<path d="m6 9 6 6 6-6"/>',
  sun: '<circle cx="12" cy="12" r="4"/><path d="M12 2v2m0 16v2M4.93 4.93l1.41 1.41m11.32 11.32 1.41 1.41M2 12h2m16 0h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41"/>',
  moon: '<path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>',
  monitor: '<rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8m-4-4v4"/>',
  menu: '<path d="M3 6h18M3 12h18M3 18h18"/>',
  mic: '<path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" x2="12" y1="19" y2="22"/>',
  cam: '<path d="m22 8-6 4 6 4V8Z"/><rect x="2" y="6" width="14" height="12" rx="2"/>',
  phone:
    '<path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>',
  logout:
    '<path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" x2="9" y1="12" y2="12"/>',
  info: '<circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/>',
  arrowRight: '<path d="M5 12h14"/><path d="m12 5 7 7-7 7"/>',
  arrowUp: '<path d="m5 12 7-7 7 7"/><path d="M12 19V5"/>',
  arrowDown: '<path d="M12 5v14"/><path d="m19 12-7 7-7-7"/>',
  folder: '<path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>',
  package:
    '<path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><path d="M3.27 6.96 12 12.01l8.73-5.05"/><path d="M12 22.08V12"/>',
  puzzle:
    '<path d="M19.44 7.85c.05-.32-.06-.65-.29-.88l-1.57-1.57a2.4 2.4 0 0 1-.71-1.7c0-.62.24-1.23.71-1.7L18.13.74a2.4 2.4 0 0 0-3.39 0l-1.57 1.57a2.4 2.4 0 0 1-1.7.71c-.62 0-1.23-.24-1.7-.71L8.2.74a2.4 2.4 0 0 0-3.39 0l-.87.87a2.4 2.4 0 0 0 0 3.4l1.57 1.57c.47.47.71 1.08.71 1.7 0 .62-.24 1.23-.71 1.7L4.17 11.6a2.4 2.4 0 0 0 0 3.39l3.2 3.2 3.81 3.81a2.4 2.4 0 0 0 3.4 0l.86-.86a2.4 2.4 0 0 0 0-3.4l-1.57-1.57a2.4 2.4 0 0 1-.71-1.7c0-.62.24-1.23.71-1.7l1.57-1.57a2.4 2.4 0 0 1 1.7-.71c.62 0 1.23.24 1.7.71l.87.87a2.4 2.4 0 0 0 3.39 0l.87-.87a2.4 2.4 0 0 0 0-3.4z"/>',
  clipboard:
    '<path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><rect x="8" y="2" width="8" height="4" rx="1"/><path d="M9 13h6"/><path d="M9 17h6"/>',
  copy: '<rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>',
  bug: '<rect x="8" y="6" width="8" height="14" rx="4"/><path d="m19 7-3 2"/><path d="m5 7 3 2"/><path d="m1 13 3 1"/><path d="m23 13-3 1"/><path d="M12 20v-4"/>',
  calendar:
    '<rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4"/><path d="M8 2v4"/><path d="M3 10h18"/>',
  user: '<path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>',
  users:
    '<path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>',
  mail: '<path d="M22 6 12 13 2 6"/><path d="M2 6a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2z"/>',
  bell: '<path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/>',
  fileText:
    '<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/><path d="M16 13H8"/><path d="M16 17H8"/>',
  ticket:
    '<path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2z"/><path d="M13 5v2"/><path d="M13 17v2"/><path d="M13 11v2"/>',
  wrench:
    '<path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>',
  hourglass:
    '<path d="M5 22h14"/><path d="M5 2h14"/><path d="M17 22v-4.17a2 2 0 0 0-.586-1.414L12 12l-4.414 4.414A2 2 0 0 0 7 17.83V22"/><path d="M7 2v4.17a2 2 0 0 0 .586 1.414L12 12l4.414-4.414A2 2 0 0 0 17 6.17V2"/>',
  star: '<path d="M12 2l3.09 6.26L22 9.27l-5 4.87L18.18 21 12 17.77 5.82 21 7 14.14l-5-4.87 6.91-1.01z"/>',
  detachCam: '<path d="m22 8-6 4 6 4V8Z"/><rect x="2" y="6" width="14" height="12" rx="2"/><path d="m1 1 22 22"/>',
  muteMic:
    '<path d="M12 3.5a3 3 0 0 0-3 3V11a3 3 0 0 0 6 0V6.5a3 3 0 0 0-3-3z"/><path d="M19 10.5v1.5a7 7 0 0 1-11.08 5.69"/><path d="M19 10.5a7 7 0 0 1-1.53 3.13"/><path d="M4.9 19.1 19.1 4.9"/><path d="M2 11v1a10 10 0 0 0 13.5 9.19"/><path d="M12 19.5V22"/>',
  hand: '<path d="M18 11V6a2 2 0 0 0-4 0v5"/><path d="M14 10V4a2 2 0 0 0-4 0v2"/><path d="M10 12V6a2 2 0 0 0-4 0v8"/><path d="M18 8a2 2 0 1 1 4 0v6a8 8 0 0 1-8 8h-2c-2.8 0-4.5-.86-5.99-2.34l-3.6-3.6a2 2 0 0 1 2.83-2.82L7 15"/>',
  smile: '<circle cx="12" cy="12" r="10"/><path d="M8 14s1.5 2 4 2 4-2 4-2"/><path d="M9 9h.01M15 9h.01"/>',
  palette:
    '<circle cx="13.5" cy="6.5" r=".5" fill="currentColor"/><circle cx="17.5" cy="10.5" r=".5" fill="currentColor"/><circle cx="8.5" cy="7.5" r=".5" fill="currentColor"/><circle cx="6.5" cy="12.5" r=".5" fill="currentColor"/><path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.93 0 1.65-.75 1.65-1.69 0-.44-.18-.84-.44-1.13-.29-.29-.44-.65-.44-1.12a1.64 1.64 0 0 1 1.67-1.67h2c3.05 0 5.55-2.5 5.55-5.55C21.97 6.01 17.46 2 12 2z"/>',
  film: '<rect x="2" y="2" width="20" height="20" rx="2.18"/><path d="M7 2v20"/><path d="M17 2v20"/><path d="M2 12h20"/><path d="M2 7h5"/><path d="M2 17h5"/><path d="M17 7h5"/><path d="M17 17h5"/>',
  camera:
    '<path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/>',
  eye: '<path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>',
  zap: '<path d="M13 2 3 14h9l-1 8 10-12h-9l1-8z"/>',
  paperclip:
    '<path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 18 8.84l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48"/>',
  image:
    '<rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="m21 15-5-5L5 21"/>',
  download:
    '<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" x2="12" y1="15" y2="3"/>',
  maximize:
    '<path d="M8 3H5a2 2 0 0 0-2 2v3"/><path d="M21 8V5a2 2 0 0 0-2-2h-3"/><path d="M3 16v3a2 2 0 0 0 2 2h3"/><path d="M16 21h3a2 2 0 0 0 2-2v-3"/>',
  share:
    '<path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/><polyline points="16 6 12 2 8 6"/><line x1="12" x2="12" y1="2" y2="15"/>',
  key: '<path d="m21 2-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0 3 3L22 7l-3-3m-3.5 3.5L19 4"/>'
};

export function icon(name: string, size = 18): string {
  const p = ICON_PATHS[name];
  if (!p) return '';
  return `<svg class="ico" width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${p}</svg>`;
}

// ── API ──────────────────────────────────────────────────
export class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

export async function api<T = unknown>(path: string, method = 'GET', body?: unknown): Promise<T> {
  const opts: RequestInit & { headers: Record<string, string> } = {
    method,
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include'
  };
  if (state.token) opts.headers.Authorization = `Bearer ${state.token}`;
  if (body !== undefined) opts.body = JSON.stringify(body);
  const res = await fetch(path, opts);
  let data: { error?: string } | null = null;
  try {
    data = await res.json();
  } catch {
    /* no body */
  }
  if (!res.ok) {
    throw new ApiError(data && data.error ? data.error : 'Request failed', res.status);
  }
  return data as T;
}

export async function loggedIn(): Promise<boolean> {
  try {
    await api('/api/auth/me');
    return true;
  } catch {
    return false;
  }
}

export async function apiUpload<T = unknown>(path: string, file: File | Blob): Promise<T> {
  const fd = new FormData();
  fd.append('file', file);
  const opts: RequestInit & { headers?: Record<string, string> } = {
    method: 'POST',
    body: fd,
    credentials: 'include'
  };
  if (state.token) opts.headers = { Authorization: `Bearer ${state.token}` };
  const res = await fetch(path, opts);
  let data: { error?: string } | null = null;
  try {
    data = await res.json();
  } catch {
    /* no body */
  }
  if (!res.ok) {
    throw new ApiError(data && data.error ? data.error : 'Upload failed', res.status);
  }
  return data as T;
}

// ── Theme ────────────────────────────────────────────────
export type ThemeOpt = 'system' | 'light' | 'dark';

export function applyTheme(opt: ThemeOpt): void {
  if (opt === 'system') {
    const dark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    document.documentElement.dataset.theme = dark ? 'dark' : 'light';
  } else {
    document.documentElement.dataset.theme = opt;
  }
  $$<HTMLButtonElement>('.theme-switch button').forEach(b => b.classList.toggle('active', b.dataset.themeOpt === opt));
}

export function initTheme(): void {
  const saved = (localStorage.getItem('wt_theme') as ThemeOpt | null) || 'system';
  applyTheme(saved);
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
    if (localStorage.getItem('wt_theme') === 'system') applyTheme('system');
  });
}

const themeSwitch = document.querySelector('.theme-switch');
if (themeSwitch) {
  themeSwitch.addEventListener('click', e => {
    const btn = (e.target as HTMLElement).closest('[data-theme-opt]') as HTMLElement | null;
    if (!btn) return;
    localStorage.setItem('wt_theme', btn.dataset.themeOpt as string);
    applyTheme(btn.dataset.themeOpt as ThemeOpt);
  });
}

// ── Routing / views ──────────────────────────────────────
const viewLoaders: Partial<Record<ViewTab, () => void>> = {};
export function registerView(tab: ViewTab, loader: () => void): void {
  viewLoaders[tab] = loader;
}

// Hook fired after every tab switch (used by Meet for picture-in-picture).
let viewChangedHook: ((tab: ViewTab) => void) | null = null;
export function setOnViewChanged(hook: ((tab: ViewTab) => void) | null): void {
  viewChangedHook = hook;
}

// Hook fired after initial data (projects/tasks/members) finishes loading.
let afterLoadHook: (() => void) | null = null;
export function setAfterLoadHook(hook: (() => void) | null): void {
  afterLoadHook = hook;
}

export function loadView(tab: ViewTab): void {
  const fn = viewLoaders[tab];
  if (fn) fn();
}

export function showAuth(): void {
  $('#appView').classList.add('hidden');
  $('#authView').classList.remove('hidden');
}

const VALID_TABS: ViewTab[] = ['dashboard', 'board', 'timer', 'projects', 'entries', 'team', 'chat', 'meet'];

export function showApp(): void {
  $('#authView').classList.add('hidden');
  $('#appView').classList.remove('hidden');
  renderProfileMenu();
  let saved: ViewTab | null = null;
  try {
    const s = localStorage.getItem('wt_tab');
    if (s && (VALID_TABS as string[]).includes(s)) saved = s as ViewTab;
  } catch {
    /* ignore */
  }
  if (saved) {
    state.view = saved;
    $$('.nav-tab').forEach(t => t.classList.toggle('active', t.dataset.tab === saved));
    $$('.tab-view').forEach(v => v.classList.remove('active'));
    const view = $(`#tab-${saved}`);
    if (view) view.classList.add('active');
  }
  loadAll();
}

export function renderProfileMenu(): void {
  const u = state.user;
  if (!u) return;
  const uname = u.name || u.username || '';
  const initial = uname.slice(0, 2).toUpperCase();
  const avatar = u.avatar || null;
  $('#profileAvatar').innerHTML = avatarHtml(initial, u.id, avatar);
  $('#profileMenuAvatar').innerHTML = avatarHtml(initial, u.id, avatar);
  $('#profileMenuName').textContent = uname || u.username;
  $('#profileMenuRole').textContent = ROLE_LABEL[u.role] || u.role;
  $('#profileDeleteBtn').classList.toggle('hidden', !avatar);
}

export function go(tab: ViewTab): void {
  state.view = tab;
  try {
    localStorage.setItem('wt_tab', tab);
  } catch {
    /* ignore */
  }
  $$('.nav-tab').forEach(t => t.classList.toggle('active', t.dataset.tab === tab));
  $$('.tab-view').forEach(v => v.classList.remove('active'));
  const view = $(`#tab-${tab}`);
  if (view) view.classList.add('active');
  window.scrollTo(0, 0);
  $('#sidebar').classList.remove('open');
  $('#navTabs').classList.remove('open');
  loadView(tab);
  if (viewChangedHook) viewChangedHook(tab);
}

// Mobile menu toggle
$('#menuBtn').addEventListener('click', () => $('#sidebar').classList.toggle('open'));

// Desktop sidebar collapse (icon rail) — gives content more room
const collapseBtn = $('#collapseBtn');
function applySidebarCollapsed(on: boolean): void {
  document.body.classList.toggle('sidebar-collapsed', on);
  if (collapseBtn) {
    collapseBtn.setAttribute('title', on ? 'Expand sidebar' : 'Collapse sidebar');
    collapseBtn.setAttribute('aria-label', on ? 'Expand sidebar' : 'Collapse sidebar');
  }
}
if (collapseBtn) {
  applySidebarCollapsed(localStorage.getItem('wt_sidebar_collapsed') === '1');
  collapseBtn.addEventListener('click', () => {
    const on = !document.body.classList.contains('sidebar-collapsed');
    localStorage.setItem('wt_sidebar_collapsed', on ? '1' : '0');
    applySidebarCollapsed(on);
  });
}
document.addEventListener('click', e => {
  const target = e.target as HTMLElement;
  if (!target.closest('#menuBtn') && !target.closest('#sidebar')) {
    $('#sidebar').classList.remove('open');
    $('#navTabs').classList.remove('open');
  }
});

// Global: nav tabs, data-goto, modal close
document.addEventListener('click', e => {
  const target = e.target as HTMLElement;
  if (target.closest('#brandLink')) {
    go('dashboard');
    return;
  }
  const tab = target.closest<HTMLElement>('.nav-tab');
  if (tab) {
    go(tab.dataset.tab as ViewTab);
    return;
  }
  const goto = target.closest<HTMLElement>('[data-goto]');
  if (goto) {
    go(goto.dataset.goto as ViewTab);
    return;
  }
  if (target.closest('.modal-close')) {
    closeModal();
    return;
  }
});

// ── Data loaders ─────────────────────────────────────────
export async function loadProjects(silent?: boolean): Promise<void> {
  const data = await api<Project[]>('/api/projects');
  state.projects = data || [];
  if (silent) return;
}

export async function loadTasks(): Promise<void> {
  const data = await api<Task[]>('/api/tasks');
  state.tasks = data || [];
}

export async function loadMembers(silent?: boolean): Promise<void> {
  try {
    const data = await api<Member[]>('/api/users');
    state.members = data || [];
  } catch {
    state.members = [];
  }
  if (silent) return;
}

export async function loadAll(): Promise<void> {
  try {
    await Promise.all([loadProjects(true), loadTasks(), loadMembers(true)]);
  } catch (e) {
    toast((e as Error).message, true);
  }
  updateRoleUI();
  loadView(state.view);
  if (afterLoadHook) afterLoadHook();
}

export function updateRoleUI(): void {
  const isAdmin = state.user && ['owner', 'admin'].includes(state.user.role);
  $('#teamTab').classList.toggle('hidden', !isAdmin);
  if (!isAdmin && state.view === 'team') go('dashboard');
}

// ── Helpers ──────────────────────────────────────────────
export function fmtMin(min: number | string): string {
  const m = Number(min) || 0;
  const h = Math.floor(m / 60);
  const r = m % 60;
  return h > 0 ? `${h}h ${r}m` : `${r}m`;
}

export function fmtHM(ms: number): string {
  const s = Math.floor(ms / 1000);
  const h = String(Math.floor(s / 3600)).padStart(2, '0');
  const m = String(Math.floor((s % 3600) / 60)).padStart(2, '0');
  const sec = String(s % 60).padStart(2, '0');
  return `${h}:${m}:${sec}`;
}

export function todayStr(): string {
  return new Date().toISOString().slice(0, 10);
}

export function daysAgoStr(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

export function fmtDate(ts: number | string | null | undefined): string {
  if (!ts) return '';
  return new Date(ts).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

export function priorityBadge(p: string): string {
  return `<span class="badge badge-priority-${esc(p || 'medium')}">${esc(p || 'medium')}</span>`;
}

export function statusBadge(s: TaskStatus): string {
  return `<span class="badge badge-status-${esc(s)}">${esc(STATUS_LABEL[s] || s)}</span>`;
}

export function typeBadge(t: TaskType): string {
  return `<span class="badge badge-type badge-type-${esc(t || 'task')}">${esc(TYPE_LABEL[t] || t || 'task')}</span>`;
}

export function memberName(m: User | Member | null): string | null {
  if (!m) return null;
  return m.name || m.username;
}

export function assigneeChip(t: Task): string {
  if (!t.assignee_id) return '';
  const n = t.assignee_name || 'Unassigned';
  const initials = n.slice(0, 2).toUpperCase();
  const member = (state.members || []).find(m => m.id === t.assignee_id);
  const avatar = member && member.avatar ? member.avatar : null;
  return `<span class="assignee-chip" title="${esc(n)}">${avatarHtml(initials, t.assignee_id, avatar)}</span>`;
}

export function resolvedByChip(t: Task): string {
  if (!t.resolved_by_id) return '';
  const n = t.resolved_by_name || 'Resolved';
  const initials = n.slice(0, 2).toUpperCase();
  const member = (state.members || []).find(m => m.id === t.resolved_by_id);
  const avatar = member && member.avatar ? member.avatar : null;
  return `<span class="assignee-chip resolved-chip" title="Resolved by ${esc(n)}">${avatarHtml(initials, t.resolved_by_id, avatar)}</span>`;
}

export function avatarHtml(initials: string, seed: number, avatar: string | null): string {
  const n = Number(seed) || 0;
  if (avatar) return `<span class="avatar avatar-photo avatar-${n % 5}"><img src="${esc(avatar)}" alt=""></span>`;
  return `<span class="avatar avatar-${n % 5}">${esc(initials)}</span>`;
}

export function projectOptions(selected?: number): string {
  return state.projects
    .map(p => `<option value="${p.id}" ${p.id === selected ? 'selected' : ''}>${esc(p.name)}</option>`)
    .join('');
}

export function taskOptions(projectId?: number | null, selected?: number): string {
  const tasks = state.tasks.filter(t => !projectId || t.project_id === projectId);
  return tasks
    .map(t => `<option value="${t.id}" ${t.id === selected ? 'selected' : ''}>${esc(t.title)}</option>`)
    .join('');
}

export function memberOptions(selected?: number): string {
  return activeMembers()
    .map(
      m =>
        `<option value="${m.id}" ${m.id === selected ? 'selected' : ''}>${esc(memberName(m))} (${esc(ROLE_LABEL[m.role] || m.role)})</option>`
    )
    .join('');
}

export function activeMembers(): Member[] {
  return (state.members || []).filter(m => m.status !== 'disabled');
}

// ── Toast ────────────────────────────────────────────────
export function toast(msg: string, kind: 'ok' | 'error' | 'info' | boolean = 'ok', hold = 3200): void {
  if (typeof kind === 'boolean') kind = kind ? 'error' : 'ok';
  const container = $('#toast');
  const tickIcon =
    ({ ok: icon('check', 15), error: icon('x', 15), info: icon('info', 15) } as Record<string, string>)[kind] ||
    icon('check', 15);
  const el = document.createElement('div');
  el.className = `toast ${kind}`;
  el.innerHTML = `<b class="tick">${tickIcon}</b><span>${esc(msg)}</span>`;
  container.appendChild(el);
  setTimeout(() => {
    el.classList.add('leaving');
    setTimeout(() => el.remove(), 260);
  }, hold);
}

// ── Loading / empty helpers ─────────────────────────────
export function loadingHtml(): string {
  return `<div class="empty-state"><div class="skeleton" style="width:56px;height:56px;border-radius:50%;"></div>
    <div class="skeleton" style="width:200px;height:14px;"></div>
    <div class="skeleton" style="width:140px;height:12px;"></div></div>`;
}

export interface EmptyCta {
  label: string;
  on: string;
}

export function emptyHtml(iconHtml: string, title: string, sub?: string, cta?: EmptyCta): string {
  return `<div class="empty-state">
    <div class="es-icon">${iconHtml}</div>
    <div class="es-title">${esc(title)}</div>
    ${sub ? `<div class="es-sub">${esc(sub)}</div>` : ''}
    ${cta ? `<button class="btn btn-primary btn-sm" ${cta.on ? `data-goto="${esc(cta.on)}"` : ''}>${esc(cta.label)}</button>` : ''}
  </div>`;
}

// ── Field validation helpers ─────────────────────────────
export function setFieldError(input: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement, msg: string): void {
  const field = input.closest('.field');
  if (!field) {
    toast(msg, true);
    return;
  }
  field.classList.add('has-error');
  const fe = field.querySelector<HTMLElement>('.field-err');
  if (fe) fe.textContent = msg;
  input.classList.add('input-error');
  if (!input.dataset._errBound) {
    input.dataset._errBound = '1';
    input.addEventListener('input', () => {
      field.classList.remove('has-error');
      input.classList.remove('input-error');
      const fe2 = field.querySelector<HTMLElement>('.field-err');
      if (fe2) fe2.textContent = '';
    });
  }
}

export function clearFieldErrors(root?: HTMLElement | Document): void {
  (root || document).querySelectorAll('.field.has-error').forEach(f => {
    f.classList.remove('has-error');
    const inp = f.querySelector('.input-error') || f.querySelector('input, select, textarea');
    if (inp) inp.classList.remove('input-error');
    const fe = f.querySelector<HTMLElement>('.field-err');
    if (fe) fe.textContent = '';
  });
}

export function fieldErrHtml(): string {
  return '<em class="field-err" role="alert"></em>';
}

// ── Modal ────────────────────────────────────────────────
export function openModal(html: string, wide?: boolean): HTMLElement {
  const card = $('#modalCard');
  card.classList.toggle('modal-wide', !!wide);
  card.innerHTML = html;
  $('#modal').classList.remove('lightbox', 'hidden');
  return card;
}

export function closeModal(): void {
  $('#modal').classList.add('hidden');
  $('#modal').classList.remove('lightbox');
  $('#modalCard').classList.remove('modal-wide', 'lightbox');
}

$('#modal').addEventListener('click', e => {
  if (e.target === $('#modal')) closeModal();
});

document.addEventListener('keydown', e => {
  if (e.key === 'Escape' && !$('#modal').classList.contains('hidden')) closeModal();
});

export function openImageLightbox(src: string): void {
  const card = $('#modalCard');
  const modal = $('#modal');
  modal.classList.add('lightbox');
  card.innerHTML = `
    <img src="${esc(src)}" alt="Preview">
    <button type="button" class="lightbox-close" title="Close">${icon('x', 20)}</button>`;
  modal.classList.remove('hidden');
  const closeBtn = card.querySelector('.lightbox-close');
  if (closeBtn) closeBtn.addEventListener('click', closeModal);
}
